import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError, formatDateTime } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { MagnifyingGlass, Trash, CheckCircle, XCircle, Shield, Barbell, User } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

const ROLE_ICONS = { admin: Shield, instructor: Barbell, client: User };

function RoleBadge({ role }) {
  const Icon = ROLE_ICONS[role] || User;
  const color = role === "admin" ? "text-[#E30613] border-[#E30613]/40"
    : role === "instructor" ? "text-[#00FF66] border-[#00FF66]/40"
    : "text-zinc-300 border-white/20";
  return (
    <span className={`inline-flex items-center gap-1 border ${color} px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold`}>
      <Icon size={10} weight="fill" />{role}
    </span>
  );
}

export default function UsersManagement() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleFilter !== "all") params.role = roleFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [roleFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const deleteUser = async (u) => {
    if (u.id === currentUser?.id) {
      toast.error(t("admin_dash.cannot_delete_self"));
      return;
    }
    if (!window.confirm(t("admin_dash.confirm_delete_user", { name: u.name, email: u.email }))) return;
    setDeletingId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(t("admin_dash.user_deleted"));
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingId(null);
    }
  };

  const total = users.length;

  return (
    <div className="space-y-4" data-testid="users-management">
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3">
        <form onSubmit={onSearch} className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("admin_dash.users_search_placeholder")}
            className="pl-9 bg-black border-white/20 rounded-none focus-visible:ring-[#E30613] focus-visible:border-[#E30613] h-11 text-sm"
            data-testid="users-search-input"
          />
        </form>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="bg-black border-white/20 rounded-none h-11 text-sm" data-testid="users-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/20 rounded-none">
            <SelectItem value="all">{t("admin_dash.users_filter_all")}</SelectItem>
            <SelectItem value="client">{t("admin_dash.users_filter_client")}</SelectItem>
            <SelectItem value="instructor">{t("admin_dash.users_filter_instructor")}</SelectItem>
            <SelectItem value="admin">{t("admin_dash.users_filter_admin")}</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={load}
          className="bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-widest text-xs font-bold rounded-none h-11 px-6"
          data-testid="users-refresh-btn"
        >
          {t("admin_dash.users_count", { count: total })}
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] text-zinc-400 text-xs uppercase tracking-widest">
          {t("common.loading")}
        </div>
      ) : users.length === 0 ? (
        <div className="border border-white/10 p-12 text-center bg-[#0A0A0A] text-zinc-400" data-testid="users-empty">
          {t("admin_dash.no_users")}
        </div>
      ) : (
        <div className="border border-white/10 bg-[#0A0A0A] overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
              <tr>
                <th className="text-left p-3">{t("admin_dash.col_name")}</th>
                <th className="text-left p-3">{t("admin_dash.col_email")}</th>
                <th className="text-left p-3">{t("admin_dash.col_roles")}</th>
                <th className="text-left p-3">{t("admin_dash.col_verified")}</th>
                <th className="text-left p-3">{t("admin_dash.col_created")}</th>
                <th className="text-right p-3">{t("admin_dash.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                  <td className="p-3 text-white flex items-center gap-2">
                    {u.name}
                    {u.is_demo && <span className="text-[9px] bg-[#FFB800] text-black px-1 font-bold" data-testid={`user-demo-${u.id}`}>{t("admin_dash.demo_badge")}</span>}
                  </td>
                  <td className="p-3 text-zinc-400 break-all">{u.email}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(u.roles || [u.role]).map((r) => <RoleBadge key={r} role={r} />)}
                    </div>
                  </td>
                  <td className="p-3">
                    {u.email_verified
                      ? <span className="inline-flex items-center gap-1 text-[#00FF66] text-xs"><CheckCircle size={12} weight="fill" />{t("admin_dash.verified_yes")}</span>
                      : <span className="inline-flex items-center gap-1 text-zinc-500 text-xs"><XCircle size={12} weight="fill" />{t("admin_dash.verified_no")}</span>}
                  </td>
                  <td className="p-3 text-zinc-400 text-xs">{u.created_at ? formatDateTime(u.created_at) : "—"}</td>
                  <td className="p-3 text-right">
                    <Button
                      onClick={() => deleteUser(u)}
                      disabled={u.id === currentUser?.id || deletingId === u.id}
                      variant="outline"
                      className="border-zinc-700 text-zinc-400 hover:bg-red-900/20 hover:border-red-500 hover:text-red-400 uppercase tracking-widest text-[10px] font-bold rounded-none h-8 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash size={12} className="mr-1" weight="bold" />{t("admin_dash.delete")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
